import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Button, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { FC, useState } from "react";

import DuplicateCardFormMUI from "web/Layout/SubscriptionReceivers/SubscriptionReceiverDuplicateCard/DuplicateCardForm/duplicateCardFormMUI";
import __ from "web/Layout/Translations";

import { as24HoursPassed } from "web/utils/page/product/subscription/checkDuplicateButtonStatus";
import { getIdFromIri } from "web/utils/page/product/subscription/iri";

import { PropsWithClasses } from "web/types/Common";
import {
  DuplicateOrderStatus,
  FormView,
  IDuplicateCardData,
  IRI,
  ISubscriptionReceiver,
} from "web/types/Subscription";
import { Nullable } from "web/types/Utils";

interface ISubscriptionReceiverDuplicateCardProps {
  data: ISubscriptionReceiver;
  duplicateCardData: IDuplicateCardData;
  enableOrderDuplicateCard: boolean;
  openDuplicateForm: Nullable<string>;
  setOpenDuplicateForm: (id: Nullable<string>) => void;
  updateReceiversDataFunc?: (newReceiverData: ISubscriptionReceiver) => void;
  viewType: FormView;
}

const SubscriptionReceiverDuplicateCardMUI: FC<
  PropsWithClasses<ISubscriptionReceiverDuplicateCardProps>
> = ({
  duplicateCardData,
  enableOrderDuplicateCard,
  data,
  openDuplicateForm,
  setOpenDuplicateForm,
  updateReceiversDataFunc,
  viewType,
}) => {
  const {
    id: receiverId,
    lastSportCardRequestAt,
    subscriptionItemReceiver,
  } = data;
  const isDuplicateFormOpen = openDuplicateForm === receiverId;
  const subscriptionItemReceiverId = getIdFromIri(
    subscriptionItemReceiver,
    IRI.RECEIVER
  );
  // Card duplicates use different ids depending on context
  // - on My Subscriptions view - receiverId
  // - on Configurator - receiver id changes with draft, so it uses subscriptionItemReceiverId
  const receiverIdForDuplicates = subscriptionItemReceiverId || receiverId;

  const [currentDuplicateOrderStatus, setCurrentDuplicateOrderStatus] =
    useState(as24HoursPassed(lastSportCardRequestAt, enableOrderDuplicateCard));

  const step2Data =
    currentDuplicateOrderStatus === DuplicateOrderStatus.JUST_ORDERED;

  const duplicatedJustOrderedStatus =
    data.duplicateOrderStatus === DuplicateOrderStatus.JUST_ORDERED ||
    step2Data;

  return (
    <>
      {duplicatedJustOrderedStatus && (
        <Box
          sx={{
            display: "flex",
            width: "380px",
            bgcolor: "#EDF7ED",
            p: 2,
            my: 1,
          }}
        >
          <CheckCircleOutlineIcon
            color="success"
            sx={{
              width: "18px",
              marginRight: "12px",
              position: "relative",
              top: "-5px",
            }}
          />
          <Typography variant="body2" sx={{ maxWidth: "200px" }}>
            {__("Zlecenie wydania duplikatu zostało przesłane do realizacji.")}
          </Typography>
        </Box>
      )}
      {!isDuplicateFormOpen && (
        <Box>
          <Button
            sx={{ my: 1 }}
            variant="outlined"
            color="primary"
            size="large"
            disabled={
              !!(openDuplicateForm && !isDuplicateFormOpen) ||
              duplicatedJustOrderedStatus
            }
            onClick={() => {
              setOpenDuplicateForm(receiverId);
            }}
          >
            {duplicatedJustOrderedStatus ? (
              <>
                {__("Zamówiono duplikat")} <CheckCircleIcon sx={{ ml: 1 }} />
              </>
            ) : (
              __("Zamów duplikat")
            )}
          </Button>
        </Box>
      )}
      {isDuplicateFormOpen && (
        // TODO: cleanup types after removing benefitForm.js
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <DuplicateCardFormMUI
          currentReceiver={data}
          duplicateCardData={duplicateCardData}
          receiverId={receiverIdForDuplicates}
          updateReceiversDataFunc={updateReceiversDataFunc}
          setOpenDuplicateForm={setOpenDuplicateForm}
          setCurrentDuplicateOrderStatus={setCurrentDuplicateOrderStatus}
          viewType={viewType}
        />
      )}
      {duplicatedJustOrderedStatus && (
        <Box sx={{ mt: 1 }}>
          {duplicateCardData.duplicateInfo && (
            <Box
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(duplicateCardData.duplicateInfo),
              }}
              className="cke_editable"
            />
          )}
        </Box>
      )}
    </>
  );
};

export default SubscriptionReceiverDuplicateCardMUI;
